import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getAxios, postAxios, putAxios } from "../../../shared/Services/commonService";
import {
  API_BOOKMAKER_ADD_RATE,
  API_BOOKMAKER_LATEST_RATE,
  API_GET_BOOKMAKER,
} from "../../../utils/api/ApiConstant";
import { useQuery } from "@tanstack/react-query";
import { Label, SubHeading } from "../../../shared/Components";
import { cleanUpValue, validateNumber } from "../../../shared/helper/common";
import {
  BookmakerMessage,
  BookmakerSetting,
  CommonModal,
} from "../../../shared/Components/Modal";
import showToast from "../../../utils/toastUtil";
import { useSelector } from "react-redux";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import useSocketContext from "../../../shared/context/socketContext";

const VOLUME = [
  {
    backVol: 0,
    layVol: 0,
  },
  {
    backVol: 0,
    layVol: 0.5,
  },
  {
    backVol: 0.5,
    layVol: 0,
  },
  {
    backVol: 0.5,
    layVol: 0.5,
  },
  {
    backVol: 0.25,
    layVol: 0.25,
  },
  {
    backVol: 0.75,
    layVol: 0.75,
  },
  {
    backVol: 0.25,
    layVol: 0.5,
  },
  {
    backVol: 0.25,
    layVol: 0.75,
  },
  {
    backVol: 0,
    layVol: 0.75,
  },
  {
    backVol: 0.25,
    layVol: 1,
  },
  {
    backVol: 0.5,
    layVol: 1.25,
  },
  {
    backVol: 0.75,
    layVol: 1.25,
  },
];
const VARIATION = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];

const BookmakerView = () => {
  const navigate = useNavigate();
  const { BookmakerSocket } = useSocketContext();
  const { user } = useSelector((state) => state.user);
  const { id } = useParams();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [rowData, setRowData] = useState(location.state?.rowOriginalData);
  const [rate, setRate] = useState(0);
  const [isShow, setIsShow] = useState({
    isOpen: false,
    rowData: null,
    name: "",
    modalTitle: "",
    modalContent: null,
    apiFlag: false,
  });
  const [volume, setVolume] = useState({
    backVol: 0,
    layVol: 0,
  });
  const [variation, setVariation] = useState(0);
  const [allRunners, setAllRunners] = useState([]);
  const [selectedRunner, setSelectedRunner] = useState(null);
  const [action, setAction] = useState(null);

  const { data: bmkLatestData } = useQuery({
    queryKey: ["bmkLatestData"],
    queryFn: async () =>
      await getAxios(`${API_BOOKMAKER_LATEST_RATE}/${id}`, {}),
  });

  const { data: bmkMarketData } = useQuery({
    queryKey: ["bmkMarketData"],
    queryFn: async () => await getAxios(`${API_GET_BOOKMAKER}/${id}`, {}),
  });

  // uses for if default state not exist then redirect to fancy page
  useEffect(() => {
    if (!location.state?.rowOriginalData) navigate("/eventmaster/bookmaker");
  }, []);

  // uses for socket only
  useEffect(() => {
    if (!BookmakerSocket) return;

    BookmakerSocket.connect();

    BookmakerSocket.emit("join-room", "room1");

    BookmakerSocket.on("bookMaker-rate", (data) => {
      // console.log("🚀 ~ BookmakerSocket.on ~ data:", data);
    });

    return () => {
      BookmakerSocket.off("bookMaker-rate"); // Clean up event listener
      BookmakerSocket.emit("leave-room", "room1"); // Use correct room name or define `id`
    };
  }, [BookmakerSocket]);

  // uses for set all runners and set maxstack data in runners
  useEffect(() => {
    if (bmkMarketData && bmkMarketData?.runners) {
      setRowData(bmkMarketData);
      const tempObj = bmkMarketData?.runners.map((runner, i) => ({
        ...runner,
        backVol: rowData?.bookmakerSetting?.maxStack,
        layVol: rowData?.bookmakerSetting?.maxStack,
      }));
      setAllRunners(tempObj);
    }
  }, [bmkMarketData]);

  useEffect(() => {
    if (
      bmkLatestData &&
      bmkMarketData?.runners &&
      bmkMarketData?.runners.length !== 0
    ) {
      setRate(bmkLatestData?.rate ?? 0);
      setVariation(bmkLatestData?.variation);
      setVolume({
        backVol: bmkLatestData?.volume?.backVol,
        layVol: bmkLatestData?.volume?.layVol,
      });

      // set runner selected
      if (bmkMarketData?.runners) {
        if (bmkLatestData?.selectedRunner === "both") {
          setSelectedRunner("both");
          handleActionRequest({
            statusVal: "both",
            bothVal: true,
            initialLatestData: bmkLatestData,
            initialSelectedRunner: {},
          });
        } else {
          const runner = bmkMarketData?.runners.find(
            (r) => r.selectionId == bmkLatestData?.selectedRunner
          );
          setSelectedRunner(runner);
          handleActionRequest({
            statusVal: "active",
            bothVal: false,
            initialLatestData: bmkLatestData,
            initialSelectedRunner: runner,
          });
        }
      }
    }
  }, [bmkLatestData, bmkMarketData?.runners]);

  const handleRateChange = (e) => {
    let value = parseInt(e.target.value);
    value = Math.max(0, value);
    setRate(value);
  };

  // memoize number array
  const numberArr = useMemo(() => {
    let previousNumbers;
    let nextNumbers;

    if (rate === "") {
      previousNumbers = [];
      nextNumbers = [];
    }

    if (rate === 0) {
      previousNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
    } else {
      previousNumbers = Array.from(
        { length: 15 },
        (_, i) => rate - (15 - i)
      ).filter((num) => num > 0);
    }

    nextNumbers = Array.from({ length: 15 }, (_, i) => rate + (i + 1)).filter(
      (num) => num > 0
    );
    return {
      previousNumbers,
      nextNumbers,
    };
  }, [rate]);

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      if (
        action !== "both" &&
        (!selectedRunner ||
          (typeof selectedRunner === "string" && !selectedRunner) ||
          (typeof selectedRunner === "object" &&
            Object.keys(selectedRunner).length === 0))
      ) {
        showToast.error("Please select runner");
        return;
      } else {
        setAction("active");
        handleActionRequest({
          statusVal: "active",
          bothVal: selectedRunner === "both" || null ? true : false,
          initialLatestData: {},
          initialSelectedRunner: {},
        });
      }
    }
  };

  const handlePutRequest = async ({
    url,
    payload,
    modalRequest = false,
    updateRowData = false,
  }) => {
    const res = await putAxios(url, payload);

    if (res && updateRowData) setRowData(res);
    if (modalRequest && res) {
      setIsShow((prev) => ({
        ...prev,
        isOpen: false,
        rowData: {},
        name: "",
      }));
    }
  };

  const handlePostRequest = async ({
    url,
    payload,
    modalRequest = false,
    isSocketListen = false,
  }) => {
    const res = await postAxios(url, payload);

    if (modalRequest && res) {
      setIsShow((prev) => ({
        ...prev,
        isOpen: false,
        rowData: {},
        name: "",
      }));
    }
  };

  const handleMarketStatus = useCallback(({ status }) => {
    let marketStatus;
    switch (status) {
      case "suspend":
        marketStatus = {
          name: "SUSPENDED",
          id: "MS940896",
        };
        break;

      case "ballstart":
        marketStatus = {
          value: "BALLSTART",
          name: "ballstart",
          id: "MS950763",
        };
        break;

      case "active":
        marketStatus = {
          name: "OPEN",
          id: "MS081893",
        };
        break;
    }

    return marketStatus;
  }, []);

  const handleActionRequest = ({
    statusVal,
    bothVal = false,
    initialLatestData = {},
    initialSelectedRunner = {},
  }) => {
    try {
      // create common seleceted runner for initial load
      let customSeletedRunner =
        Object.keys(initialSelectedRunner).length !== 0
          ? initialSelectedRunner
          : selectedRunner;
      let customRate = validateNumber(initialLatestData?.rate || rate);
      let customVariation = validateNumber(
        initialLatestData?.variation || variation
      );
      let customVolume = initialLatestData?.volume || volume;

      // return false if statusVal is null or undefined or ""
      if (statusVal === null || statusVal === undefined || statusVal === "")
        return;

      // return false if runner is not selected and not both
      if (
        statusVal !== "ballstart" &&
        statusVal !== "both" &&
        !customSeletedRunner &&
        !bothVal
      ) {
        showToast.error("Please select runner");
        return;
      }

      // create common runner
      let modifyRunners;

      // set runners according to actions
      if (bothVal) {
        modifyRunners = allRunners.map((runner) => ({
          ...runner,
          backRate: customRate + (customVolume?.backVol ?? 0),
          layRate: 0,
          status: {
            name: "open",
            id: "MS081893",
          },
        }));
        // setSelectedRunner(null);
      } else {
        const findIndex = allRunners.findIndex(
          (x) => x.selectionId == customSeletedRunner.selectionId
        );

        if (findIndex >= 0) {
          modifyRunners = allRunners.map((runner, index) => {
            if (index === findIndex) {
              return {
                ...runner,
                backRate: customRate + (customVolume?.backVol ?? 0),
                layRate:
                  customRate +
                  ((customVolume?.layVol ?? 0) + (customVariation ?? 0)),
                status: {
                  name: "open",
                  id: "MS081893",
                },
              };
            } else {
              return {
                ...runner,
                backRate: 0,
                layRate: 0,
                status: {
                  id: "MS940896",
                  name: "suspend",
                  value: "SUSPENDED",
                },
              };
            }
          });
        }
      }
      setAllRunners(modifyRunners);

      // handle add bookmaker rate request
      const payload = {
        marketId: id,
        rate: customRate,
        variation: customVariation,
        runners: modifyRunners,
        volume: customVolume,
        selectedRunner: bothVal ? "both" : customSeletedRunner.selectionId,
        marketStatus: handleMarketStatus({ status: statusVal }),
        userId: user?.user_id,
        userName: user?.username,
      };

      handlePostRequest({
        url: API_BOOKMAKER_ADD_RATE,
        payload,
        isSocketListen: true,
      });
    } catch (error) {
      console.log("🚀 ~ handleActionRequest ~ error:", error);
    }
  };

  return (
    <>
      <SubHeading
        subTitle={`Bookmaker [ ${cleanUpValue(rowData?.match?.name)} ]`}
        isAddBtn={false}
      />
      <div className="row m-0 p-0">
        <div className="col-lg-12  m-0 p-1 ">
          <div className="setting_but_box mb-1">
            <Button
              className=" btn_main"
              onClick={() =>
                setIsShow((prev) => ({
                  ...prev,
                  isOpen: true,
                  rowData,
                  modalTitle: "Message",
                  modalContent: BookmakerMessage,
                }))
              }
            >
              Message
            </Button>
            <Button
              className=" btn_main"
              onClick={() =>
                setIsShow((prev) => ({
                  ...prev,
                  isOpen: true,
                  rowData,
                  modalTitle: "Bookmaker Setting",
                  modalContent: BookmakerSetting,
                }))
              }
            >
              Config
            </Button>
          </div>
        </div>
        <div className="col-lg-6 col-12  m-0 p-1">
          <div className="row  m-0 p-0 ">
            <div className=" col-lg-12  m-0 p-0 ">
              <div
                className=" cursor-point card m-0 mb-1 p-2 pb-1 d-flex justify-content-between  flex-row"
                onClick={() => setIsVisible(!isVisible)}
              >
                <h6 className="p-0 m-0 font-weight-bold">Bookmaker Rate</h6>
                <MdOutlineKeyboardArrowDown fontSize={23} />
              </div>
              <div
                className={`card p-0 m-0 mb-2 ${
                  isVisible ? "d-block" : "d-none"
                }`}
              >
                <div className="row m-0 p-1 ">
                  <div className="col-lg-6 col-12  m-0 p-0 pe-1 pb-1 ">
                    <h6 className="m-0 p-0">Previous Numbers</h6>
                    <div className="previous_box">
                      {numberArr?.previousNumbers.map((item, i) => (
                        <Button key={i} className=" previous-but">
                          {item}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="col-lg-6 col-12  m-0 p-0 pe-1 pb-1">
                    <h6 className="m-0 p-0">Next Numbers</h6>
                    <div className="previous_box">
                      {numberArr?.nextNumbers.map((item, i) => (
                        <Button key={i} className="previous-but">
                          {item}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-12  m-0 p-0 ">
              <div className="card p-1 m-0 mb-1">
                <div className="row m-0 p-0">
                  <div className="col-lg-12 col-12  m-0 p-0 ">
                    <div className="sport_bookmaker_box">
                      {allRunners &&
                        allRunners.map((runner, i) => (
                          <div className="market_name_box" key={i}>
                            <p className="text-name">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="runner"
                                value={runner}
                                onChange={() => {
                                  setSelectedRunner(runner);
                                  setAction("suspend");
                                  // handleActionRequest({
                                  //   statusVal: "suspend",
                                  //   bothVal: false,
                                  //   initialSelectedRunner: runner,
                                  // });
                                }}
                                checked={
                                  selectedRunner?.selectionId ===
                                  runner?.selectionId
                                }
                                id={runner?.selectionId}
                              />
                              <Label htmlFor={runner?.selectionId}>
                                {runner?.runnerName}
                              </Label>
                            </p>

                            {/* market_suspend => ul tag class for suspend */}
                            <ul
                              className={`market_sport ${
                                action === "suspend"
                                  ? "market_suspend"
                                  : action === "ballstart"
                                  ? "market_ball_running"
                                  : ""
                              }`}
                            >
                              <li className="market market_bg-a">
                                <p>
                                  {runner?.backRate ?? 0}
                                  <span>{runner?.backVol ?? 0}</span>
                                </p>
                              </li>
                              <li className="market market_bg-b">
                                <p>
                                  {runner?.layRate ?? 0}
                                  <span>{runner?.layVol ?? 0}</span>
                                </p>
                              </li>
                            </ul>
                          </div>
                        ))}
                    </div>
                  </div>
                  <div className="col-lg-12 col-12  m-0 p-0 pt-2 ">
                    <div className="w-100  position-relative">
                      <input
                        type="number"
                        value={rate}
                        className="form-control w-100 py-2 form-select-lg text-center"
                        onChange={handleRateChange}
                        onKeyDown={handleEnter}
                      />
                    </div>
                    <div className="book_running_box">
                      <div className="row m-0 p-0 justify-content-between ">
                        <div className="col-lg-6 col-6 m-0 p-0 pb-1 pe-1">
                          <Button
                            className={`running_but btn-danger ball_but`}
                            onClick={() => {
                              setAction("ballstart");
                              handleActionRequest({
                                statusVal: "ballstart",
                                bothVal: selectedRunner === null ? false : true,
                              });
                            }}
                          >
                            Ball
                          </Button>
                        </div>
                        <div className="col-lg-6 col-6 m-0 p-0 pb-1">
                          <Button
                            className={`running_but btn-success active_but`}
                            onClick={() => {
                              setAction("active");
                              handleActionRequest({
                                statusVal: "active",
                                bothVal:
                                  selectedRunner === "both" || null
                                    ? true
                                    : false,
                              });
                            }}
                          >
                            Active
                          </Button>
                        </div>
                        <div className="col-lg-4 col-4 m-0 p-0 pe-1 ">
                          <Button
                            className={`running_but`}
                            onClick={() => {
                              setAction("suspend");
                              handleActionRequest({
                                statusVal: "suspend",
                                bothVal: selectedRunner === null ? false : true,
                              });
                            }}
                          >
                            Suspend
                          </Button>
                        </div>
                        <div className="col-lg-4 col-4 m-0 p-0">
                          <Button
                            className="running_but"
                            onClick={() => {
                              setSelectedRunner("both");
                              setAction("both");
                              handleActionRequest({
                                statusVal: "both",
                                bothVal: true,
                              });
                            }}
                          >
                            Both
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-12  m-0 p-0 ">
              <div className="row m-0 p-0">
                <div className="col-lg-6 col-12  m-0 p-0 pe-1 pd-1">
                  <div className="card p-1 m-0 mb-1">
                    <h6 className="m-0 p-1">Volume</h6>
                    <div className="previous_box">
                      {VOLUME.map((item, i) => {
                        return (
                          <Button
                            key={i}
                            className={`previous-but ${
                              volume.backVol === item.backVol &&
                              volume.layVol === item.layVol
                                ? "active"
                                : ""
                            } `}
                            onClick={() =>
                              setVolume((prev) => ({
                                ...prev,
                                backVol: item.backVol,
                                layVol: item.layVol,
                              }))
                            }
                          >{`${item.backVol}/${item.layVol}`}</Button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 col-12  m-0 p-0 ">
                  <div className="card p-1 m-0 mb-1">
                    <h6 className="m-0 p-1">Variation</h6>
                    <div className="previous_box">
                      {VARIATION.map((item, i) => {
                        return (
                          <Button
                            key={i}
                            className={`previous-but ${
                              variation === item ? "active" : ""
                            }`}
                            onClick={() => setVariation(item)}
                          >
                            {item}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-12 m-0 p-1">
          <div className="card p-0 m-0">
            <h6 className="m-0 p-2">Matched bet</h6>
            <div className="respo-scroll table_book">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th className=" text-left">User Name</th>
                    <th className=" text-left">Runner</th>
                    <th className="text-right "> Run</th>
                    <th className="text-right "> Stake</th>
                  </tr>
                </thead>
                <tbody>
                  {/* <tr className="matched-runner-bg-color-detail1">
                    <td className="text-left">demo</td>
                    <td className="text-left">
                      <span>
                        do not bet / 1000
                        <span className="bet-list-span">24/05/25:37:14px</span>
                      </span>
                    </td>
                    <td className="text-right">17</td>
                    <td className="text-right">500</td>
                  </tr>
                  <tr className="matched-runner-bg-color-detail2">
                    <td className="text-left">demo</td>
                    <td className="text-left">
                      <span>
                        do not bet / 1000
                        <span className="bet-list-span">24/05/25:37:14px</span>
                      </span>
                    </td>
                    <td className="text-right">17</td>
                    <td className="text-right">500</td>
                  </tr> */}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {isShow?.isOpen && (
        <CommonModal
          isShow={isShow.isOpen}
          handleShowHide={() =>
            setIsShow((prev) => ({
              ...prev,
              isOpen: false,
              rowData: {},
              name: "",
            }))
          }
          modalTitle={isShow.modalTitle}
        >
          {isShow.modalContent &&
            React.createElement(isShow.modalContent, {
              handleShowHide: () =>
                setIsShow((prev) => ({
                  ...prev,
                  isOpen: false,
                  rowData: {},
                  name: "",
                  modalTitle: "",
                  apiFlag: !prev.apiFlag,
                })),
              rowData: isShow.rowData,
              handlePutRequest,
            })}
        </CommonModal>
      )}
    </>
  );
};

export default BookmakerView;
